import React, { useEffect, useState, useCallback } from "react";
import { ShieldCheck, Ban, Check, X, Banknote, Trophy, Plus, Flag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const RED_C = { fulfilled: "text-[#16A34A]", pending_review: "text-[#FFB300]", approved: "text-[#00E5FF]", paid: "text-[#16A34A]", rejected: "text-[#FF0055]" };

export default function AdminRewards() {
  const [reds, setReds] = useState(null);
  const [contests, setContests] = useState([]);
  const [payouts, setPayouts] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState({ title: "", desc: "", sponsor: "", metric: "wallet", days: 3, prize1: "$100", prize2: "$50", prize3: "$25" });

  const load = useCallback(async () => {
    try {
      const [r, c] = await Promise.all([api.get("/admin/redemptions"), api.get("/contests")]);
      setReds(r.data.redemptions); setPayouts(r.data.payouts_enabled); setContests(c.data.contests);
    } catch (e) {
      if ([401, 403].includes(e?.response?.status)) setForbidden(true); else toast.error(errMsg(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const moderate = async (id, action) => {
    let note = "";
    if (action !== "mark_paid") note = window.prompt(`Note for ${action}:`, "") || "";
    setBusy(id + action);
    try { await api.post("/admin/redemptions/moderate", { redemption_id: id, action, note }); toast.success(`Marked ${action}`); await load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  const createContest = async (e) => {
    e.preventDefault();
    setBusy("create");
    try {
      const prizes = [
        { place: 1, label: form.prize1 }, { place: 2, label: form.prize2 }, { place: 3, label: form.prize3 },
      ].filter((p) => p.label);
      await api.post("/admin/contests", { title: form.title, desc: form.desc, sponsor: form.sponsor || null, metric: form.metric, days: Number(form.days), prizes, funded_by: form.sponsor ? "sponsor" : "pool" });
      toast.success("Contest created"); setForm({ ...form, title: "", desc: "", sponsor: "" }); await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  const endContest = async (id) => {
    if (!window.confirm("End this contest and lock in winners?")) return;
    setBusy(id);
    try { await api.post(`/admin/contests/${id}/end`); toast.success("Contest ended, winners snapshotted"); await load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  if (forbidden) return (
    <div className="min-h-[100dvh] bg-[#F8FAFC] flex flex-col items-center justify-center text-center px-6" data-testid="admin-forbidden">
      <Ban className="text-[#FF0055]" size={40} /><h1 className="font-display text-2xl mt-3">Admin only</h1>
    </div>
  );

  return (
    <div className="min-h-[100dvh] bg-[#F8FAFC] px-5 py-6 max-w-2xl mx-auto" data-testid="admin-rewards-page">
      <div className="flex items-center gap-2"><ShieldCheck className="text-[#FFD700]" size={24} /><h1 className="font-display text-2xl">Rewards & Contests</h1></div>
      <div className="mt-1 label-xs">Real payouts: <span className={payouts ? "text-[#16A34A]" : "text-[#FFB300]"}>{payouts ? "ENABLED" : "disabled (legal-gated)"}</span></div>

      {/* Redemptions */}
      <h2 className="font-display text-lg mt-5 mb-2 flex items-center gap-2"><Banknote size={16} className="text-[#16A34A]" /> Redemption queue</h2>
      {reds === null ? <div className="flex justify-center py-6 text-slate-500"><Loader2 className="animate-spin" /></div>
        : reds.length === 0 ? <p className="text-slate-400 label-xs">No redemptions yet.</p>
        : <div className="space-y-2">
            {reds.map((r) => (
              <div key={r.id} data-testid={`admin-red-${r.id}`} className="bg-white border border-slate-200 rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold">{r.title}</div>
                  <span className={`label-xs ${RED_C[r.status] || "text-slate-500"}`}>{r.status}</span>
                </div>
                <div className="label-xs text-slate-500 mt-0.5">{r.kind} · −{r.points_spent} HP{r.cash_usd ? ` · $${r.cash_usd}` : ""}</div>
                {r.kind === "cash" && r.status === "pending_review" && (
                  <div className="mt-2 flex gap-2">
                    <button data-testid={`red-approve-${r.id}`} disabled={!!busy} onClick={() => moderate(r.id, "approve")} className="flex-1 label-xs bg-[#00E5FF]/15 text-[#00E5FF] py-1.5 rounded-lg font-bold flex items-center justify-center gap-1"><Check size={12} /> Approve</button>
                    <button data-testid={`red-paid-${r.id}`} disabled={!!busy} onClick={() => moderate(r.id, "mark_paid")} className="flex-1 label-xs bg-[#16A34A] text-white py-1.5 rounded-lg font-bold">Mark paid</button>
                    <button data-testid={`red-reject-${r.id}`} disabled={!!busy} onClick={() => moderate(r.id, "reject")} className="flex-1 label-xs bg-[#2a2a2a] text-[#FF0055] py-1.5 rounded-lg font-bold flex items-center justify-center gap-1"><X size={12} /> Reject</button>
                  </div>
                )}
                {r.kind === "cash" && r.status === "approved" && (
                  <button data-testid={`red-paid-${r.id}`} disabled={!!busy} onClick={() => moderate(r.id, "mark_paid")} className="mt-2 w-full label-xs bg-[#16A34A] text-white py-1.5 rounded-lg font-bold">Mark paid</button>
                )}
              </div>
            ))}
          </div>}

      {/* Contests */}
      <h2 className="font-display text-lg mt-7 mb-2 flex items-center gap-2"><Trophy size={16} className="text-[#FF0055]" /> Contests</h2>
      <form onSubmit={createContest} className="bg-white border border-slate-200 rounded-xl p-3 space-y-2">
        <input data-testid="ct-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title (e.g. GTBank Business Challenge)" className="w-full bg-black/40 border border-slate-200 rounded-lg px-3 py-2 text-sm" />
        <input data-testid="ct-desc" required value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} placeholder="Description" className="w-full bg-black/40 border border-slate-200 rounded-lg px-3 py-2 text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <input data-testid="ct-sponsor" value={form.sponsor} onChange={(e) => setForm({ ...form, sponsor: e.target.value })} placeholder="Sponsor (optional)" className="bg-black/40 border border-slate-200 rounded-lg px-3 py-2 text-sm" />
          <select data-testid="ct-metric" value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value })} className="bg-black/40 border border-slate-200 rounded-lg px-3 py-2 text-sm">
            <option value="wallet">Net worth</option><option value="clout">Clout</option><option value="level">Level</option><option value="lifetime_points">Hustler Points</option>
          </select>
        </div>
        <div className="grid grid-cols-4 gap-2">
          <input value={form.prize1} onChange={(e) => setForm({ ...form, prize1: e.target.value })} placeholder="🥇" className="bg-black/40 border border-slate-200 rounded-lg px-2 py-2 text-sm" />
          <input value={form.prize2} onChange={(e) => setForm({ ...form, prize2: e.target.value })} placeholder="🥈" className="bg-black/40 border border-slate-200 rounded-lg px-2 py-2 text-sm" />
          <input value={form.prize3} onChange={(e) => setForm({ ...form, prize3: e.target.value })} placeholder="🥉" className="bg-black/40 border border-slate-200 rounded-lg px-2 py-2 text-sm" />
          <input type="number" min="1" value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} placeholder="days" className="bg-black/40 border border-slate-200 rounded-lg px-2 py-2 text-sm" />
        </div>
        <button data-testid="ct-create" disabled={busy === "create"} type="submit" className="w-full bg-[#FFD700] text-slate-900 font-bold py-2 rounded-lg flex items-center justify-center gap-1 label-xs"><Plus size={14} /> Create contest</button>
      </form>
      <div className="mt-2 space-y-2">
        {contests.map((c) => (
          <div key={c.id} data-testid={`admin-ct-${c.id}`} className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{c.title}</div>
              <div className="label-xs text-slate-500">{c.sponsor ? `${c.sponsor} · ` : ""}{c.metric} · {c.status}</div>
            </div>
            {c.status === "active" && <button data-testid={`ct-end-${c.id}`} disabled={!!busy} onClick={() => endContest(c.id)} className="label-xs bg-[#FF0055]/15 text-[#FF0055] px-3 py-1.5 rounded-lg font-bold flex items-center gap-1"><Flag size={12} /> End</button>}
          </div>
        ))}
      </div>
    </div>
  );
}
